import { Button, ButtonProps, CircularProgress, Tooltip } from "@mui/material"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { TRADE_TICKET_LOCATORS } from "@/App/Equities/mobile/components/tradeScreen/TradeTicket/locators"
import { useInstrument } from "@/App/Equities/shared/services/instruments"
import {
  OrderType,
  submit,
  toggleDirection,
  useLimitPriceValue,
  useOrderType,
  useQtyValue,
  useResponse,
  useStopPriceValue,
} from "@/App/Equities/shared/services/orders"
import { Box, Stack } from "@/generated/material"
import { Direction } from "@/generated/TradingGateway"

const TooltipTitle = ({
  symbol,
  direction,
}: {
  symbol: string
  direction: string
}) => {
  const displayName = useInstrument(symbol)?.displayName

  return (
    <Box display="flex">
      <Box mr="xs">{direction}</Box>
      {displayName}
    </Box>
  )
}

const isEmpty = (value: string): boolean => !value || value === "0"

interface SubmitOrderButtonProps extends ButtonProps {
  direction: Direction
  symbol: string
}

export const SubmitOrderButton = ({
  direction,
  symbol,
  fullWidth,
  ...props
}: SubmitOrderButtonProps) => {
  const { t } = useTranslation()
  const type = useOrderType()
  const qtyValue = useQtyValue()
  const stopPriceValue = useStopPriceValue()
  const limitPriceValue = useLimitPriceValue()

  const { isLoading } = useResponse()

  const showLoading = isLoading && (
    <CircularProgress color="secondary" size="15px" />
  )

  useEffect(() => {
    toggleDirection(direction)
  }, [direction])

  const checkStopOrderValue = isEmpty(stopPriceValue) || isEmpty(qtyValue)
  const checkLimitOrderValue = isEmpty(limitPriceValue) || isEmpty(qtyValue)

  const orderTypeChecks = {
    [OrderType.MarketOrder]: isEmpty(qtyValue),
    [OrderType.LimitOrder]: checkLimitOrderValue,
    [OrderType.StopOrder]: checkStopOrderValue,
    [OrderType.StopLimitOrder]: checkStopOrderValue,
  }

  const disabled = orderTypeChecks[type] || false

  const onClick = () =>
    submit({
      symbol,
      direction,
      type,
    })

  return (
    <Stack
      paddingY="sm"
      width="100%"
      alignItems={fullWidth ? "stretch" : "flex-end"}
    >
      <Tooltip
        title={
          <TooltipTitle
            symbol={symbol}
            direction={t(direction.toLowerCase())}
          />
        }
      >
        <span>
          <Button
            variant="brand"
            fullWidth={fullWidth}
            data-testid={TRADE_TICKET_LOCATORS.tradeButton}
            disabled={disabled || isLoading || !symbol}
            onClick={onClick}
            endIcon={showLoading}
            {...props}
          >
            {t(direction.toLowerCase())} {symbol}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  )
}
